import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const SC_BRUEHL_LOGO = 'https://scbruehl.ch/wp-content/themes/scbruehl/assets/img/scb_logo.svg';

export default function ResetPasswordPage() {
    const { token } = useParams();
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState('form'); // form, success, error
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password.length < 6) {
            toast.error('Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        if (password !== passwordConfirm) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }

        setIsLoading(true);
        try {
            await axios.post(`${API_URL}/auth/reset-password`, { token, new_password: password });
            setStatus('success');
        } catch (error) {
            const msg = error.response?.data?.detail || 'Passwort konnte nicht geändert werden.';
            setErrorMessage(msg);
            setStatus('error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-background min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-md animate-scale-in">
                <div className="text-center mb-8">
                    <img src={SC_BRUEHL_LOGO} alt="SC Brühl St. Gallen" className="w-20 h-20 mx-auto mb-4" />
                    <h1 className="text-4xl font-heading text-white tracking-tight">Kronentipp</h1>
                    <p className="text-green-200 mt-2 font-body">SC Brühl St. Gallen - Tippspiel</p>
                </div>

                <Card className="glass-card border-white/10">
                    {status === 'success' && (
                        <CardContent className="pt-8 pb-8 text-center space-y-5">
                            <div className="flex justify-center">
                                <div className="w-16 h-16 rounded-full bg-[#00ad43]/20 flex items-center justify-center">
                                    <CheckCircle className="w-9 h-9 text-[#00ad43]" />
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold" data-testid="reset-success-title">Passwort geändert</h2>
                            <p className="text-muted-foreground text-sm">Dein Passwort wurde erfolgreich geändert.</p>
                            <Link to="/login">
                                <Button className="btn-primary gap-2 mt-2" data-testid="reset-login-button">
                                    Jetzt anmelden
                                </Button>
                            </Link>
                        </CardContent>
                    )}

                    {status === 'error' && (
                        <CardContent className="pt-8 pb-8 text-center space-y-5">
                            <div className="flex justify-center">
                                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                                    <XCircle className="w-9 h-9 text-red-500" />
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold text-red-600" data-testid="reset-error-title">Fehler</h2>
                            <p className="text-muted-foreground text-sm">{errorMessage}</p>
                            <Link to="/forgot-password">
                                <Button variant="outline" className="gap-2 mt-2" data-testid="reset-retry-button">
                                    Erneut anfordern
                                </Button>
                            </Link>
                        </CardContent>
                    )}

                    {status === 'form' && (
                        <>
                            <CardHeader className="space-y-1">
                                <CardTitle className="text-2xl text-center">Neues Passwort</CardTitle>
                                <p className="text-center text-sm text-muted-foreground">
                                    Gib dein neues Passwort ein.
                                </p>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Neues Passwort *</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="password"
                                                type="password"
                                                placeholder="Mindestens 6 Zeichen"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="pl-10"
                                                required
                                                data-testid="reset-password-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="passwordConfirm">Passwort bestätigen *</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="passwordConfirm"
                                                type="password"
                                                placeholder="Passwort wiederholen"
                                                value={passwordConfirm}
                                                onChange={(e) => setPasswordConfirm(e.target.value)}
                                                className="pl-10"
                                                required
                                                data-testid="reset-password-confirm-input"
                                            />
                                        </div>
                                    </div>
                                    <Button 
                                        type="submit" 
                                        className="w-full btn-primary"
                                        disabled={isLoading}
                                        data-testid="reset-submit-button"
                                    >
                                        {isLoading ? 'Wird gespeichert...' : 'Passwort ändern'}
                                    </Button>
                                </form>
                            </CardContent>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}
